import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Sparkles, Upload, Download, ArrowRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageSeo } from "@/components/PageSeo";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";

// Hero images in public folder for faster LCP - discoverable in initial HTML
// Use smaller image for mobile, full size for desktop
const heroSizes = "(max-width: 768px) 100vw, (max-width: 1200px) 55vw, 1000px";
const heroWebpSrcSet = [
  "/images/hero-room-480.webp 480w",
  "/images/hero-room-800.webp 800w",
  "/images/hero-room-1000.webp 1000w",
  "/images/hero-room-1200.webp 1200w",
].join(", ");
const heroJpgSrcSet = [
  "/images/hero-room-480.jpg 480w",
  "/images/hero-room-800.jpg 800w",
  "/images/hero-room-1000.jpg 1000w",
  "/images/hero-room-1200.jpg 1200w",
].join(", ");

const beforeAfterSizes = "(max-width: 768px) 100vw, 50vw";
const beforeWebpSrcSet = [
  "/images/before-room-480.webp 480w",
  "/images/before-room-800.webp 800w",
].join(", ");
const beforeJpgSrcSet = [
  "/images/before-room-480.jpg 480w",
  "/images/before-room-665.jpg 665w",
  "/images/before-room-800.jpg 800w",
].join(", ");
const afterWebpSrcSet = [
  "/images/after-room-480.webp 480w",
  "/images/after-room-800.webp 800w",
  "/images/after-room-1200.webp 1200w",
].join(", ");
const afterJpgSrcSet = [
  "/images/after-room-480.jpg 480w",
  "/images/after-room-665.jpg 665w",
  "/images/after-room-800.jpg 800w",
  "/images/after-room-1200.jpg 1200w",
].join(", ");

const Index = () => {
  const { t } = useTranslation();
  const { buildPath } = useLocalizedPath();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PageSeo />
      
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="container mx-auto px-4 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 sm:mb-6">
                {t('hero.title')}
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-muted-foreground mb-6 sm:mb-8">
                {t('hero.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to={buildPath('/auth')} className="w-full sm:w-auto">
                  <Button size="lg" className="gap-2 w-full sm:w-auto">
                    <Sparkles className="h-5 w-5" />
                    {t('hero.tryFree')}
                  </Button>
                </Link>
                <Link to={buildPath('/examples')} className="w-full sm:w-auto">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto">
                    {t('hero.seeExamples')}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative order-first lg:order-last">
              <picture>
                <source type="image/webp" srcSet={heroWebpSrcSet} sizes={heroSizes} />
                <source type="image/jpeg" srcSet={heroJpgSrcSet} sizes={heroSizes} />
                <img
                  src="/images/hero-room-800.jpg"
                  alt={t('hero.heroImageAlt')}
                  className="rounded-lg shadow-2xl w-full"
                  width={800}
                  height={450}
                  fetchPriority="high"
                  decoding="async"
                />
              </picture>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="bg-muted/20 py-12 sm:py-16 lg:py-20">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-foreground mb-8 sm:mb-12">
              {t('howItWorks.title')}
            </h2>
            <p className="text-center text-muted-foreground mb-8 max-w-3xl mx-auto">
              {t('howItWorks.description')}
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-4xl mx-auto">
              <Card className="p-6 text-center">
                <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Upload className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">{t('howItWorks.step1Title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('howItWorks.step1Description')}
                </p>
              </Card>
              <Card className="p-6 text-center">
                <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">{t('howItWorks.step2Title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('howItWorks.step2Description')}
                </p>
              </Card>
              <Card className="p-6 text-center">
                <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Download className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">{t('howItWorks.step3Title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('howItWorks.step3Description')}
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Before/After */}
        <section className="container mx-auto px-4 py-12 sm:py-16 lg:py-20">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-foreground mb-8 sm:mb-12">
            {t('beforeAfter.title')}
          </h2>
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
            <div>
              <p className="text-sm font-semibold text-muted-foreground mb-2">{t('beforeAfter.before')}</p>
              <picture>
                <source type="image/webp" srcSet={beforeWebpSrcSet} sizes={beforeAfterSizes} />
                <source type="image/jpeg" srcSet={beforeJpgSrcSet} sizes={beforeAfterSizes} />
                <img
                  src="/images/before-room-665.jpg"
                  alt={t('beforeAfter.beforeAlt')}
                  className="rounded-lg shadow-lg w-full"
                  width={665}
                  height={443}
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground mb-2">{t('beforeAfter.after')}</p>
              <picture>
                <source type="image/webp" srcSet={afterWebpSrcSet} sizes={beforeAfterSizes} />
                <source type="image/jpeg" srcSet={afterJpgSrcSet} sizes={beforeAfterSizes} />
                <img
                  src="/images/after-room-665.jpg"
                  alt={t('beforeAfter.afterAlt')}
                  className="rounded-lg shadow-lg w-full"
                  width={665}
                  height={443}
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="bg-muted/20 py-12 sm:py-16 lg:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                {t('aboutSection.title')}
              </h2>
              <p className="text-muted-foreground mb-6">
                {t('aboutSection.description')}
              </p>
              <Link to={buildPath('/about')}>
                <Button variant="outline" className="gap-2">
                  {t('aboutSection.learnMore')}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-primary text-primary-foreground py-12 sm:py-16 lg:py-20">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              {t('cta.title')}
            </h2>
            <p className="text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 opacity-90">
              {t('cta.description')}
            </p>
            <Link to={buildPath('/auth')}>
              <Button size="lg" variant="secondary" className="gap-2 w-full sm:w-auto">
                <Sparkles className="h-5 w-5" />
                {t('cta.createAccount')}
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
